import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { motion } from 'framer-motion';
import { Globe, GripVertical, Plus, Search, Trash2 } from 'lucide-react';
import { Fragment, ReactNode, useEffect, useMemo, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { twJoin } from 'tailwind-merge';
import { Assistant, AssistantPatchRequest, AssistantPostRequest, teamAPI } from '../../api/team';
import { AlgoIcon } from '../../assets/icons/algoIcon';
import { MODULE_TYPES } from '../../constants';
import { ModuleWithMirror, useModules } from '../../hooks/useModules';
import { useTeam } from '../../hooks/useTeam';
import { AccessScopesEnum } from '../module-detail/ModuleForm';
import Button from '../utils/Button';
import Checkbox from '../utils/Checkbox';
import { Modal } from '../utils/modals/Modal';
import { globalFilterFn } from '../utils/react-table/utils';
import { Skeleton } from '../utils/Skeleton';
import { Body1, Body2, Caption2, H2, H4 } from '../utils/typo';

export type AssistantTypes = keyof typeof ASSISTANT_MAP;

const ASSISTANT_MAP = {
  CONTENT: 'Content',
  AI_SCRIBE: 'AI Scribe',
} as const;

export function AssistantsSettingSection() {
  const [selected, setSelected] = useState(0);
  const [open, setOpen] = useState(false);

  return (
    <>
      <div className='space-y-4'>
        <Menu selected={selected} setSelected={setSelected} />
        <div className='flex gap-4 rounded-lg bg-gray-100 px-4 py-2'>
          <div className='grow'>
            <H4 className='text-gray-700'>
              Manage assistants for {Object.values(ASSISTANT_MAP)[selected]}.
            </H4>
            <Caption2 className='flex h-6 items-center'>
              Drag and drop to reorder assistants. The order you set here matches how assistants
              appear in the {Object.values(ASSISTANT_MAP)[selected]} menu.
            </Caption2>
          </div>
          <div className='flex items-center'>
            <Button
              className='flex h-10 items-center !px-6 py-2 hover:bg-primary-600'
              onClick={() => setOpen(true)}
            >
              <Plus size={20} className='stroke-white' />
              <p className='text-button-1'>Add</p>
            </Button>
          </div>
        </div>
      </div>

      <div>
        <AssistantList selected={selected} />
      </div>
      <AssistantModal open={open} onClose={() => setOpen(false)} selected={selected} />
    </>
  );
}

function AssistantList({ selected }: { selected: number }) {
  const { team, isTeamLoading } = useTeam();
  const queryKey = ['assistants', team.id, Object.keys(ASSISTANT_MAP)[selected]];
  const { data, refetch } = useQuery({
    queryKey,
    queryFn: teamAPI.getAssistants,
    initialData: [],
  });

  const queryClient = useQueryClient();
  const { mutateAsync: patchAsync } = useMutation({
    mutationFn: (data: AssistantPatchRequest) => teamAPI.patchAssistants(data),
    onMutate: async (patchData) => {
      await queryClient.cancelQueries({ queryKey });
      const previousTodos = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, (old: Assistant[]) => {
        const newList = [...old];
        const fromIndex = newList.findIndex((item) => item.id === patchData.assistantId);
        if (fromIndex === -1) return old;

        const [movedItem] = newList.splice(fromIndex, 1);
        newList.splice(patchData.position, 0, movedItem);
        return newList;
      });
      return { previousTodos };
    },
    onError: (_err, _updatedItem, context) => {
      queryClient.setQueryData(queryKey, context?.previousTodos);
    },
    onSettled: () => refetch(),
  });
  const { mutateAsync: deleteAsync } = useMutation({
    mutationFn: teamAPI.deleteAssistants,
  });

  const onDragEnd = async ({ source, destination }) => {
    if (!destination) return;

    const patchData = {
      assistantId: data[source.index].id,
      position: destination.index,
    };
    await patchAsync(patchData);
  };

  const onRemove = async (id: number) => {
    await deleteAsync(id);
    await refetch();
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId='AssistantListDroppable' key='AssistantListDroppable'>
        {(provided) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            className='select-none space-y-4 pt-9'
          >
            {data.filter((item) => item.type === Object.keys(ASSISTANT_MAP)[selected]).length >
            0 ? (
              data
                .filter((item) => item.type === Object.keys(ASSISTANT_MAP)[selected])
                .map((assistant, index) => (
                  <Draggable draggableId={`item-${index}`} key={`item-${index}`} index={index}>
                    {(provided) => (
                      <div
                        className='flex items-center gap-2'
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        ref={provided.innerRef}
                      >
                        <H4 className='text-gray-700'>{String(index + 1).padStart(2, '0')}</H4>
                        <div className='flex grow items-center rounded-lg border border-gray-300 bg-white py-6 pr-4'>
                          <GripVertical size={24} className='stroke-gray-600' />
                          <div className='flex grow items-center gap-2'>
                            {assistant.module.icon ? (
                              <img
                                src={assistant.module.icon}
                                alt={'algo preview'}
                                className='h-8 w-8 overflow-hidden rounded object-cover'
                              />
                            ) : (
                              <div className='flex h-8 w-8 items-center justify-center rounded-sm border border-gray-200'>
                                <AlgoIcon />
                              </div>
                            )}
                            <H4 className='text-gray-900'>{assistant.module.name}</H4>
                          </div>
                          <div className='relative'>
                            <Trash2
                              size={24}
                              className='peer cursor-pointer stroke-gray-600 hover:stroke-red-500'
                              onClick={() => onRemove(assistant.id)}
                            />
                            <Body2 className='absolute -right-0 top-7 z-10 hidden whitespace-nowrap rounded-sm bg-gray-800 px-2 py-0.5 text-white peer-hover:block'>
                              Remove from List
                            </Body2>
                          </div>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))
            ) : (
              <div className='flex h-96 items-center justify-center'>
                <Body1 className='text-gray-700'>No assistants for Content.</Body1>
              </div>
            )}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
}

function Menu({
  selected,
  setSelected,
}: {
  selected: number;
  setSelected: (index: number) => void;
}) {
  const {
    team: { ambient_listening_enabled },
  } = useTeam();
  return (
    <div className='flex gap-4'>
      {Object.values(ASSISTANT_MAP).map((item, idx) => {
        const isActive = selected === idx;
        const disabled = !ambient_listening_enabled && idx === 1;
        return (
          <H2
            key={idx}
            onClick={() => !disabled && setSelected(idx)}
            className={twJoin(
              'relative select-none',
              disabled ? 'text-gray-300' : 'cursor-pointer',
              !isActive && 'text-gray-300'
            )}
          >
            {item}
            {isActive && (
              <motion.div
                layoutId='menuHighlight'
                className='absolute bottom-0 left-0 h-0.5 w-full bg-primary-600'
              />
            )}
          </H2>
        );
      })}
    </div>
  );
}

function AssistantModal({
  open,
  onClose,
  selected,
}: {
  open: boolean;
  onClose: () => void;
  selected: number;
}) {
  const { team } = useTeam();
  const { data, refetch } = useQuery({
    queryKey: ['assistants', team.id, Object.keys(ASSISTANT_MAP)[selected]],
    queryFn: teamAPI.getAssistants,
    initialData: [],
  });
  const { modules, isLoading } = useModules();
  const selectableModules = useMemo(
    () =>
      modules.filter(
        (module) =>
          module.type === MODULE_TYPES.ALGO &&
          !data.some((assistant) => assistant.module.id === module.id)
      ),
    [data, modules]
  );
  const [globalFilter, setGlobalFilter] = useState('');

  const columns = useMemo<ColumnDef<ModuleWithMirror>[]>(
    () => [
      {
        id: 'checkbox',
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
        size: 24,
      },
      {
        id: 'title',
        header: 'Title',
        accessorKey: 'title',
        cell: (cell) => {
          const accessScope = cell.row.getValue('access_scope') as AccessScopesEnum;
          const team = cell.row.getValue('team') as { name: string };
          return (
            <span className='whitespace-pre-wrap'>
              {cell.renderValue() as ReactNode}
              {[AccessScopesEnum.GLOBAL, AccessScopesEnum.SPECIFIC_TEAMS].includes(accessScope) && (
                <span className='group relative ml-1 inline-flex h-5 items-center rounded-sm bg-gray-100 pl-1 align-middle'>
                  <Globe size={16} className='stroke-gray-600' />
                  <Caption2 className='p-0.5 pr-1 text-gray-700'>Shared</Caption2>
                  <Body2 className='absolute left-0 top-full z-10 hidden whitespace-nowrap rounded-sm bg-gray-800 px-2 py-0.5 text-white group-hover:block'>
                    Shared from {team.name}
                  </Body2>
                </span>
              )}
            </span>
          );
        },
        size: 864,
      },
      { id: 'access_scope', accessorKey: 'access_scope' },
      { id: 'team', accessorKey: 'team' },
    ],
    [isLoading]
  );

  const tableColumns = useMemo(
    () =>
      isLoading
        ? columns.map((column) => ({
            ...column,
            cell: () => <Skeleton className='h-[24px] bg-gray-200' />,
          }))
        : columns,
    [columns, isLoading]
  );

  const tableData = useMemo(
    () => (isLoading ? Array(1).fill({}) : selectableModules || []),
    [isLoading, selectableModules]
  );

  const { getHeaderGroups, getRowModel, getSelectedRowModel, toggleAllRowsSelected } =
    useReactTable({
      columns: tableColumns,
      data: tableData,
      getCoreRowModel: getCoreRowModel(),
      getRowId: (row) => row.id,
      initialState: { columnVisibility: { access_scope: false, team: false } },
      state: {
        globalFilter,
      },
      globalFilterFn,
      getFilteredRowModel: getFilteredRowModel(),
    });

  const { mutateAsync, isPending: isMutating } = useMutation({
    mutationFn: (data: AssistantPostRequest) => teamAPI.postAssistants(data),
    onSuccess: () => refetch(),
  });
  const selectedRows = getSelectedRowModel();

  const onAdd = async () => {
    const postData = {
      team: team.id,
      modules: [...Object.keys(selectedRows.rowsById).map((id) => Number(id))],
      type: Object.keys(ASSISTANT_MAP)[selected] as AssistantTypes,
    };
    await mutateAsync(postData);
    onClose();
  };

  useEffect(() => {
    setGlobalFilter('');
    toggleAllRowsSelected(false);
  }, [open]);

  return (
    <Modal onClose={onClose} open={open} className='overflow-y-hidden p-0 shadow-04' size='large'>
      <Modal.Head className='px-4 py-3 text-heading-3' onClose={onClose}>
        Select to Display as Assistant in {Object.values(ASSISTANT_MAP)[selected]}
      </Modal.Head>
      <Modal.Body className='py-2'>
        <div className='mx-4 flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2.5'>
          <Search size={20} className='stroke-gray-800' />
          <input
            className='m-0 !h-[20px] w-full !border-none text-[14px] leading-[20px] text-gray-900 placeholder-gray-300 shadow-none focus:border-none focus:outline-none'
            type='text'
            placeholder='Search'
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
          />
        </div>
        <div className='h-[500px] overflow-y-auto px-4'>
          <table className='mb-10 w-full'>
            {getHeaderGroups().map((headerGroup, idx) => (
              <thead className='border-b-2 border-gray-300' key={idx}>
                <tr>
                  {headerGroup.headers.map((header) =>
                    header.isPlaceholder ? null : (
                      <th
                        key={header.id}
                        className='px-2 py-4 text-left'
                        style={{ width: header.getSize() }}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    )
                  )}
                </tr>
              </thead>
            ))}
            <tbody>
              {!isLoading && getRowModel().rows.length == 0 ? (
                <td colSpan={2} className='text-center'>
                  No data
                </td>
              ) : (
                getRowModel().rows.map((row) => (
                  <Fragment key={row.id}>
                    <tr className='select-none border-b border-gray-300'>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className='px-[8px] py-[12px]'>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  </Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Modal.Body>
      <Modal.Footer className='flex justify-end gap-2 px-4 py-3'>
        <div className='flex'>
          <Button.Reverse onClick={onClose}>Cancel</Button.Reverse>
        </div>
        <div className='flex'>
          <Button onClick={onAdd} className='!px-8' disabled={isMutating}>
            Add {selectedRows.rows.length > 0 && `(${selectedRows.rows.length})`}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
